import { Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { finalize, first, lastValueFrom } from 'rxjs';

import { AuthApiService } from './auth-api.service';
import {
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventName,
  GoogleAnalyticsService,
} from '../../core';
import { User } from '../model/auth.model';
import { Permission, RoleType } from '../model/role.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #router = inject(Router);
  readonly #authApiService = inject(AuthApiService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #analyticsService = inject(GoogleAnalyticsService);

  readonly #isLoginPendingSignal = signal(false);
  #user: User | null = null;
  #token: string | null = null;
  #refreshToken: string | null = null;
  #accessToken: string | null = null;

  get isLoginPending() {
    return this.#isLoginPendingSignal.asReadonly();
  }

  get token() {
    return this.#token;
  }

  get user() {
    return this.#user;
  }

  get isHost() {
    return this.hasRole(RoleType.ROOM_HOST);
  }

  get isAdmin() {
    return this.hasRole(RoleType.ADMIN);
  }

  get roles() {
    return this.#user?.roles || [];
  }

  get refreshToken() {
    return this.#refreshToken;
  }

  get accessToken() {
    return this.#accessToken;
  }

  get costCenter() {
    return this.#user?.costCenter || '';
  }

  public init() {
    const id_token = localStorage.getItem('id_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const accessToken = localStorage.getItem('access_token');
    const roles = localStorage.getItem('roles');
    const costCenter = localStorage.getItem('costCenter');

    if (id_token) {
      this.#token = id_token;
      this.#refreshToken = refreshToken;
      this.#accessToken = accessToken;
      this.#user = this.#parseJwt(this.#token);
      let currentRoles = [];
      if (roles) {
        try {
          currentRoles = JSON.parse(roles);
        } catch (err) {
          console.error('Error parsing roles', err);
        }
      }
      this.#user = {
        ...this.#parseJwt(this.#token),
        roles: currentRoles,
        costCenter: costCenter || '',
      };

      return;
    }

    this.#activatedRoute.queryParams
      .pipe(first(params => 'code' in params))
      .subscribe(() => {
        const queryParams = this.#activatedRoute.snapshot.queryParams;
        void this.#router.navigate(['/']);
        this.requestToken(queryParams);
      });
  }

  hasRole(requiredRole: RoleType): boolean {
    if (!this.#user) return false;
    return this.roles.some(role => role.roleType === requiredRole);
  }

  hasPermissions(permissions: Permission[]): boolean {
    if (!this.#user) return false;
    return this.roles.some(role =>
      permissions.every(p => role.permissions.includes(p))
    );
  }

  hasPermissionsInRole(
    roleType: RoleType | undefined,
    permissions: Permission[]
  ): boolean {
    if (!this.#user) return false;
    if (!roleType) return false;
    // Get all roles of the specified type
    const rolesOfType = this.roles.filter(role => role.roleType === roleType);
    if (rolesOfType.length === 0) return false;
    // Concatenate permissions from all matching roles
    const allPermissionsSet = new Set<Permission>(
      rolesOfType.flatMap(role => role.permissions)
    );
    // Check if every required permission is present
    return permissions.every(p => allPermissionsSet.has(p));
  }

  public login() {
    this.#authApiService.redirectToGoogle();
  }

  public refresh(idToken: string, accessToken: string) {
    localStorage.setItem('id_token', idToken);
    localStorage.setItem('access_token', accessToken);
    this.#token = idToken;
    this.#accessToken = accessToken;
  }

  public async logout() {
    this.#isLoginPendingSignal.set(true);
    const deviceToken = localStorage.getItem('deviceToken');
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      await lastValueFrom(
        this.#authApiService.logout(deviceToken, refreshToken)
      );
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    localStorage.clear();
    this.#user = null;
    this.#isLoginPendingSignal.set(false);
    void this.#router.navigate(['/login']);
  }

  public requestToken(authCode: Params) {
    this.#isLoginPendingSignal.set(true);
    this.#authApiService
      .requestToken(authCode)
      .pipe(
        finalize(() => {
          this.#isLoginPendingSignal.set(false);
        })
      )
      .subscribe(resp => {
        if (resp.idtoken !== 'Please provide a code') {
          this.#analyticsService.trackEvent(
            GoogleAnalyticsEventName.LoginSuccessful,
            GoogleAnalyticsEventCategory.Login,
            `Roles: ${JSON.stringify(resp.authorization.roles)}`
          );
          localStorage.setItem('id_token', resp.idtoken);
          localStorage.setItem('refresh_token', resp.refreshtoken);
          localStorage.setItem('access_token', resp.access_token);
          localStorage.setItem('costCenter', resp.costCenter || '');
          localStorage.setItem(
            'roles',
            JSON.stringify(resp.authorization.roles)
          );
          this.#token = resp.idtoken;
          this.#refreshToken = resp.refreshtoken;
          this.#accessToken = resp.access_token;
          this.#user = {
            ...this.#parseJwt(this.#token),
            roles: resp.authorization.roles,
            costCenter: resp.costCenter || '',
          };
          const entryRoute = sessionStorage.getItem('entryRoute');
          sessionStorage.removeItem('entryRoute');
          void this.#router.navigateByUrl(entryRoute ? entryRoute : '/');
        }
      });
  }

  #parseJwt(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(code => '%' + ('00' + code.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT token:', e);
      return null;
    }
  }
}
